/* ============================================================
   IMAGE TOOLS — client-side optimization before upload. Downscales
   to a sensible max edge and re-encodes to WebP, so Storage holds
   lean, web-ready images regardless of what the owner drops in.
   ============================================================ */

const MAX_EDGE = 2000 // longest side, px
const QUALITY = 0.82

// Resize + re-encode an image File to a WebP Blob. Returns the blob
// plus its dimensions. Falls back to the original if the browser can't
// decode/encode it (e.g. SVG, HEIC without support).
export async function optimizeImage(file) {
  if (!file.type.startsWith('image/')) {
    throw new Error('That file is not an image.')
  }
  // SVGs are already small + vector — upload as-is.
  if (file.type === 'image/svg+xml') {
    return { blob: file, type: file.type, width: 0, height: 0, ext: 'svg' }
  }

  const bitmap = await loadBitmap(file)
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))
  const width = Math.round(bitmap.width * scale)
  const height = Math.round(bitmap.height * scale)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  ctx.drawImage(bitmap, 0, 0, width, height)
  if (bitmap.close) bitmap.close()

  const blob = await new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Encoding failed.'))),
      'image/webp',
      QUALITY,
    ),
  )
  return { blob, type: 'image/webp', width, height, ext: 'webp' }
}

function loadBitmap(file) {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file)
  }
  // Fallback for older browsers.
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not read that image.'))
    }
    img.src = url
  })
}

// Derive a clean media name + id from a filename.
export function deriveName(filename) {
  const base = filename.replace(/\.[^.]+$/, '')
  return (
    base
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toUpperCase()
      .slice(0, 40) || 'UPLOAD'
  )
}
